import { create } from 'zustand'
import type { IApiClient } from '../api'
import type {
  DaemonHealth,
  Interaction,
  InteractionAnswer,
  KnowledgeEntry,
  NormalizedRun,
  QueueEntry,
  ProjectedAgent,
  ProjectedChat,
  Task,
  TranscriptEvent,
} from '../api/types'
import type { WorldStateProjection } from '../world/worldStateClient'
import type { PresenceConnectionStatus } from '../world/worldSocketClient'
import type { WorldMember } from '../../shared/worldProtocol'

export type WorkplacePhoneTab = 'places' | 'agents' | 'work' | 'people' | 'systems'
export type WorkplacePhoneDestination = 'live-work' | 'launchable-tasks'

const LS_SENSITIVITY   = 'animus.sensitivity'
const LS_AUDIO_MUTED   = 'animus.audioMuted'
const LS_MASTER_VOLUME = 'animus.masterVolume'
const LS_VIEW_BOB      = 'animus.viewBob'
const LS_MINIMAP       = 'animus.minimap'
const LS_ONBOARDED     = 'animus.onboarded'
let workplacePhoneRequestNonce = 0

function nextWorkplacePhoneRequestNonce(): number {
  workplacePhoneRequestNonce += 1
  return workplacePhoneRequestNonce
}

function loadSensitivity(): number {
  try {
    const v = localStorage.getItem(LS_SENSITIVITY)
    if (v !== null) {
      const n = Number(v)
      if (Number.isFinite(n) && n > 0) return n
    }
  } catch {
    // localStorage unavailable (test env, SSR)
  }
  return 1
}

function saveSensitivity(v: number): void {
  try {
    localStorage.setItem(LS_SENSITIVITY, String(v))
  } catch {
    // localStorage unavailable
  }
}

function loadBool(key: string, dflt: boolean): boolean {
  try {
    const v = localStorage.getItem(key)
    if (v !== null) return v === 'true'
  } catch { /* localStorage unavailable */ }
  return dflt
}

function saveBool(key: string, v: boolean): void {
  try { localStorage.setItem(key, String(v)) } catch { /* ignore */ }
}

function loadVolume(): number {
  try {
    const v = localStorage.getItem(LS_MASTER_VOLUME)
    if (v !== null) {
      const n = Number(v)
      if (Number.isFinite(n) && n >= 0 && n <= 1) return n
    }
  } catch { /* ignore */ }
  return 1
}

function saveVolume(v: number): void {
  try { localStorage.setItem(LS_MASTER_VOLUME, String(v)) } catch { /* ignore */ }
}

let toastSeq = 0

/** Shared empty list so absent runs don't mint a new array identity per poll. */
const EMPTY_INTERACTIONS: Interaction[] = Object.freeze([]) as unknown as Interaction[]

/** Shallow content equality for the interactions map (ids + responded state). */
export function sameInteractions(
  a: Record<string, Interaction[]>,
  b: Record<string, Interaction[]>,
): boolean {
  if (a === b) return true
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const key of aKeys) {
    const av = a[key]
    const bv = b[key]
    if (!bv || av.length !== bv.length) return false
    for (let i = 0; i < av.length; i++) {
      if (av[i].id !== bv[i].id || av[i].respondedAt !== bv[i].respondedAt) return false
    }
  }
  return true
}

function bucketInteractions(
  runs: readonly NormalizedRun[],
  interactions: readonly Interaction[],
): Record<string, Interaction[]> {
  const buckets: Record<string, Interaction[]> = {}
  for (const run of runs) {
    if (run.status === 'running') buckets[run.id] = EMPTY_INTERACTIONS
  }
  for (const interaction of interactions) {
    const bucket = buckets[interaction.runId]
    buckets[interaction.runId] = bucket && bucket !== EMPTY_INTERACTIONS
      ? [...bucket, interaction]
      : [interaction]
  }
  return buckets
}

export interface Toast {
  id: string
  runId: string
  title: string
  status: 'completed' | 'failed'
}

export interface AssetProgress {
  loaded: number
  total: number
  coreReady: boolean
  done: boolean
}

export type InteractionResponseFailure =
  | 'not-authorized'
  | 'conflict'
  | 'unavailable'
  | 'session-changed'

export type InteractionResponseResult =
  | { status: 'accepted'; refreshed: boolean }
  | { status: 'already-answered'; refreshed: true }
  | { status: 'failed'; reason: InteractionResponseFailure }

export type SubjectLaunchFailure =
  | 'not-authorized'
  | 'conflict'
  | 'unavailable'
  | 'session-changed'

export type SubjectLaunchState =
  | { status: 'pending' }
  | { status: 'accepted'; runId: string; observed: boolean }
  | { status: 'failed'; reason: SubjectLaunchFailure }

export type SubjectLaunchResult =
  | { status: 'accepted'; runId: string; observed: boolean }
  | { status: 'failed'; reason: SubjectLaunchFailure }

const TERMINAL_RUN_STATUSES = new Set<NormalizedRun['status']>([
  'completed',
  'failed',
  'cancelled',
])

/**
 * Reconcile local command feedback with the authoritative run projection.
 *
 * A successful launch may not appear in the first read-after-write snapshot.
 * Keep that command in its explicit syncing state until a later hydrate sees
 * the exact returned run id. Once the canonical run is terminal, remove the
 * local guard so the subject can be launched again. Missing rows are never
 * treated as completion because a bounded Portal page may omit an active run.
 */
export function reconcileSubjectLaunches(
  launches: Readonly<Record<string, SubjectLaunchState>>,
  runs: readonly NormalizedRun[],
): Record<string, SubjectLaunchState> {
  const byId = new Map(runs.map((run) => [run.id, run]))
  let next: Record<string, SubjectLaunchState> | undefined

  for (const [taskId, launch] of Object.entries(launches)) {
    if (launch.status !== 'accepted') continue
    const run = byId.get(launch.runId)
    if (!run) continue
    if (TERMINAL_RUN_STATUSES.has(run.status)) {
      next ??= { ...launches }
      delete next[taskId]
    } else if (!launch.observed) {
      next ??= { ...launches }
      next[taskId] = { ...launch, observed: true }
    }
  }

  return next ?? launches as Record<string, SubjectLaunchState>
}

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null || !('status' in error)) return undefined
  const status = (error as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

interface ArenaState {
  /** API client (set after auth) */
  apiClient: IApiClient | null
  /**
   * Monotonic ownership generation for user-derived remote data. Any async
   * operation that started under an older generation must not commit.
   */
  apiGeneration: number
  setApiClient: (client: IApiClient | null) => void

  /** Arena-owned durable world projection, never Portal application data. */
  worldStateProjection: WorldStateProjection | null
  worldStateStatus: 'disconnected' | 'connecting' | 'ready' | 'degraded'
  setWorldStateProjection: (projection: WorldStateProjection) => void
  setWorldStateStatus: (status: ArenaState['worldStateStatus']) => void

  /** Arena-owned ephemeral roster of other authenticated users in this world. */
  presenceStatus: PresenceConnectionStatus
  setPresenceStatus: (status: PresenceConnectionStatus) => void
  activeWorldMembers: WorldMember[]
  setActiveWorldMembers: (members: WorldMember[]) => void
  /** Arena room identity used by spatial UI; never projected to Portal. */
  currentWorldRoomId: string
  setCurrentWorldRoomId: (roomId: string) => void

  /** Freshness of the disposable Portal application projection. */
  remoteProjectionStatus: 'disconnected' | 'loading' | 'fresh' | 'stale'
  remoteProjectionCapturedAt: string | null
  markRemoteProjectionStale: () => void

  /** GLB preload progress (drives the title-screen loader) */
  assetProgress: AssetProgress
  setAssetProgress: (p: AssetProgress) => void

  /** Daemon health */
  health: DaemonHealth | null

  /** Active runs */
  runs: NormalizedRun[]

  /** Queue entries */
  queue: QueueEntry[]
  tasks: Task[]
  /** Command feedback only; canonical activity always comes from `runs`. */
  subjectLaunchesByTaskId: Record<string, SubjectLaunchState>
  knowledge: KnowledgeEntry[]
  agents: ProjectedAgent[]
  /** Actor-authorized generic conversations. */
  chats: ProjectedChat[]
  /** Commit one canonical mutation response into the shared chat projection. */
  upsertChat: (chat: ProjectedChat) => void
  runDetailsById: Record<string, NormalizedRun>
  loadRunDetail: (runId: string) => Promise<void>

  /** Live transcript events, keyed by runId */
  transcriptsByRun: Record<string, TranscriptEvent[]>

  /** Pending interactions, keyed by runId */
  interactionsByRun: Record<string, Interaction[]>

  /** Currently "observed" run in the FPV world */
  focusedRunId: string | null
  setFocusedRunId: (id: string | null) => void

  /** Whether the pointer is locked (playing) */
  isPlaying: boolean
  setIsPlaying: (v: boolean) => void

  /** Whether the game is paused (pointer lock exited while in-game) */
  isPaused: boolean
  setIsPaused: (v: boolean) => void

  /** Whether the world-owned agent conversation dialog is open. */
  isAgentTalkOpen: boolean
  setIsAgentTalkOpen: (v: boolean) => void
  /** One-shot deep link consumed by the existing world-owned agent dialog. */
  agentTalkRequest: { agentId: string; nonce: number } | null
  requestAgentTalk: (agentId: string) => void
  consumeAgentTalkRequest: () => void

  /** Whether the Arena-owned workplace phone is open. */
  isWorkplacePhoneOpen: boolean
  setIsWorkplacePhoneOpen: (v: boolean) => void
  /** One-shot room-owned deep link into a specific phone application. */
  workplacePhoneRequest: {
    tab: WorkplacePhoneTab
    destination?: WorkplacePhoneDestination
    nonce: number
  } | null
  requestWorkplacePhone: (
    tab: WorkplacePhoneTab,
    destination?: WorkplacePhoneDestination,
  ) => void
  consumeWorkplacePhoneRequest: (nonce: number) => void

  /** Mouse look sensitivity (1 = default); persisted to localStorage */
  mouseSensitivity: number
  setMouseSensitivity: (v: number) => void

  /** Interaction currently open in the in-game dialog (null = no dialog) */
  activeInteraction: Interaction | null
  setActiveInteraction: (interaction: Interaction | null) => void

  /** Whether the player is within interact range of a waiting agent */
  nearWaitingStation: boolean
  setNearWaitingStation: (near: boolean) => void

  /** Nearest station's run id when within proximity range (null = none near) */
  nearestStationRunId: string | null
  setNearestStationRunId: (id: string | null) => void

  /** Run currently open in the inspector panel (null = panel closed) */
  inspectorRunId: string | null
  /**
   * Tab toggle: closes the panel when open; opens it on the nearest station's
   * run when playing, near a station, and no dialog/pause menu is open.
   */
  toggleInspector: () => void
  /**
   * Open the inspector on a specific run (gallery board / desk [E] path).
   * Gated like toggleInspector: playing, not paused, no dialog open.
   */
  openInspector: (runId: string) => void
  closeInspector: () => void

  /** Audio muted flag; persisted to localStorage */
  audioMuted: boolean
  setAudioMuted: (v: boolean) => void

  /** Master volume 0–1; persisted to localStorage */
  masterVolume: number
  setMasterVolume: (v: number) => void

  /** View bob while walking; persisted to localStorage */
  viewBobEnabled: boolean
  setViewBobEnabled: (v: boolean) => void

  /**
   * Player world pose, pushed from the engine update loop each frame (with a
   * small dead-zone so a stationary player doesn't re-render the wayfinding UI).
   * X/Z are world metres; yaw is radians, 0 = facing north (-Z), clockwise.
   */
  playerX: number
  playerZ: number
  playerYaw: number
  setPlayerPose: (x: number, z: number, yaw: number) => void

  /** Whether the corner minimap is shown; toggled with [M], persisted. */
  minimapVisible: boolean
  toggleMinimap: () => void

  /**
   * Whether the first-visit onboarding hint has been dismissed. Set true on the
   * player's first real interaction and persisted so it never shows again.
   */
  onboardingDismissed: boolean
  dismissOnboarding: () => void

  /** Active completion toasts (max 3) */
  toasts: Toast[]
  addToast: (t: Omit<Toast, 'id'>) => void
  dismissToast: (id: string) => void

  /** Hydrate runs + health + interactions from API */
  hydrate: () => Promise<void>

  /** Launch authorized work and then refresh the canonical Portal projection. */
  launchTaskRun: (taskId: string) => Promise<SubjectLaunchResult>

  /** Append a transcript event (keeps last 50 to bound memory) */
  appendTranscriptEvent: (event: TranscriptEvent) => void
  replaceTranscriptEvents: (runId: string, events: TranscriptEvent[]) => void

  /** Overwrite interactions for a single run */
  setInteractionsForRun: (runId: string, interactions: Interaction[]) => void

  /**
   * Submit one actor-owned interaction response without optimistic success.
   * Only a confirmed 2xx, or a conflict followed by an authoritative refresh
   * proving the interaction is gone, may clear pending world state.
   */
  submitInteractionResponse: (
    interaction: Interaction,
    answer: InteractionAnswer,
  ) => Promise<InteractionResponseResult>
}

export const useArenaStore = create<ArenaState>((set, get) => ({
  apiClient: null,
  apiGeneration: 0,
  worldStateProjection: null,
  worldStateStatus: 'disconnected',
  presenceStatus: 'disconnected',
  activeWorldMembers: [],
  currentWorldRoomId: 'district',
  setWorldStateProjection: (projection) => set({
    worldStateProjection: projection,
    worldStateStatus: 'ready',
  }),
  setWorldStateStatus: (worldStateStatus) => set({ worldStateStatus }),
  setPresenceStatus: (presenceStatus) => set((state) =>
    state.presenceStatus === presenceStatus ? state : { presenceStatus }),
  setActiveWorldMembers: (activeWorldMembers) => set((state) =>
    state.activeWorldMembers === activeWorldMembers ||
      (state.activeWorldMembers.length === 0 && activeWorldMembers.length === 0)
      ? state
      : { activeWorldMembers }),
  setCurrentWorldRoomId: (currentWorldRoomId) => set({ currentWorldRoomId }),
  remoteProjectionStatus: 'disconnected',
  remoteProjectionCapturedAt: null,
  markRemoteProjectionStale: () => set((state) => ({
    remoteProjectionStatus: state.apiClient ? 'stale' : 'disconnected',
    // Identity/catalog data may remain useful as a last-good read model, but
    // uncertain activity must never keep animating as if it were live.
    health: null,
    queue: [],
    interactionsByRun: {},
    runs: state.runs.map((run) =>
      TERMINAL_RUN_STATUSES.has(run.status) || run.stale
        ? run
        : { ...run, stale: true }),
  })),
  assetProgress: { loaded: 0, total: 0, coreReady: false, done: false },
  setAssetProgress: (p) => set({ assetProgress: p }),
  setApiClient: (client) => set((state) => {
    if (state.apiClient === client) return state
    return {
      apiClient: client,
      apiGeneration: state.apiGeneration + 1,
      worldStateProjection: null,
      worldStateStatus: client ? 'connecting' : 'disconnected',
      presenceStatus: 'disconnected',
      activeWorldMembers: [],
      currentWorldRoomId: 'district',
      remoteProjectionStatus: client ? 'loading' : 'disconnected',
      remoteProjectionCapturedAt: null,

      // Everything below is derived from the authenticated Portal identity.
      // Clear it atomically before the next user's requests can begin.
      health: null,
      runs: [],
      queue: [],
      tasks: [],
      subjectLaunchesByTaskId: {},
      knowledge: [],
      agents: [],
      chats: [],
      runDetailsById: {},
      transcriptsByRun: {},
      interactionsByRun: {},
      focusedRunId: null,
      activeInteraction: null,
      isAgentTalkOpen: false,
      agentTalkRequest: null,
      isWorkplacePhoneOpen: false,
      workplacePhoneRequest: null,
      nearWaitingStation: false,
      nearestStationRunId: null,
      inspectorRunId: null,
      toasts: [],
    }
  }),

  health: null,
  runs: [],
  queue: [],
  tasks: [],
  subjectLaunchesByTaskId: {},
  knowledge: [],
  agents: [],
  chats: [],
  upsertChat: (chat) => set((state) => {
    const index = state.chats.findIndex((candidate) => candidate.id === chat.id)
    if (index < 0) return { chats: [chat, ...state.chats] }
    const chats = [...state.chats]
    chats[index] = chat
    return { chats }
  }),
  runDetailsById: {},
  transcriptsByRun: {},
  interactionsByRun: {},
  focusedRunId: null,
  isPlaying: false,
  isPaused: false,
  isAgentTalkOpen: false,
  agentTalkRequest: null,
  isWorkplacePhoneOpen: false,
  workplacePhoneRequest: null,
  mouseSensitivity: loadSensitivity(),
  activeInteraction: null,
  nearWaitingStation: false,
  nearestStationRunId: null,
  inspectorRunId: null,

  audioMuted:     loadBool(LS_AUDIO_MUTED, false),
  masterVolume:   loadVolume(),
  viewBobEnabled: loadBool(LS_VIEW_BOB, true),
  playerX:        0,
  playerZ:        0,
  playerYaw:      0,
  minimapVisible: loadBool(LS_MINIMAP, true),
  onboardingDismissed: loadBool(LS_ONBOARDED, false),
  toasts:         [],

  setFocusedRunId: (id) => set({ focusedRunId: id }),
  setIsPlaying:   (v) => set({ isPlaying: v }),
  setIsPaused:    (v) => set({ isPaused: v }),
  setIsAgentTalkOpen: (v) => set({ isAgentTalkOpen: v }),
  requestAgentTalk: (agentId) => set((state) => ({
    agentTalkRequest: {
      agentId,
      nonce: (state.agentTalkRequest?.nonce ?? 0) + 1,
    },
  })),
  consumeAgentTalkRequest: () => set({ agentTalkRequest: null }),
  setIsWorkplacePhoneOpen: (v) => set({ isWorkplacePhoneOpen: v }),
  requestWorkplacePhone: (tab, destination) => set((state) => {
    if (
      !state.isPlaying || state.isPaused || state.isAgentTalkOpen ||
      state.activeInteraction !== null || state.inspectorRunId !== null
    ) return state
    return {
      isWorkplacePhoneOpen: true,
      workplacePhoneRequest: {
        tab,
        ...(destination ? { destination } : {}),
        nonce: nextWorkplacePhoneRequestNonce(),
      },
    }
  }),
  consumeWorkplacePhoneRequest: (nonce) => set((state) =>
    state.workplacePhoneRequest?.nonce === nonce
      ? { workplacePhoneRequest: null }
      : state),
  setMouseSensitivity: (v) => {
    saveSensitivity(v)
    set({ mouseSensitivity: v })
  },
  setActiveInteraction: (interaction) => set({ activeInteraction: interaction }),
  setNearWaitingStation: (near) => set({ nearWaitingStation: near }),
  setNearestStationRunId: (id) => set({ nearestStationRunId: id }),

  toggleInspector: () => {
    const { inspectorRunId, isPlaying, isPaused, activeInteraction, nearestStationRunId } = get()
    if (inspectorRunId !== null) {
      set({ inspectorRunId: null })
      return
    }
    if (!isPlaying) return
    if (isPaused) return
    if (activeInteraction !== null) return
    if (nearestStationRunId === null) return
    set({ inspectorRunId: nearestStationRunId })
  },
  openInspector: (runId) => {
    const { isPlaying, isPaused, activeInteraction } = get()
    if (!isPlaying) return
    if (isPaused) return
    if (activeInteraction !== null) return
    set({ inspectorRunId: runId })
  },
  closeInspector: () => set({ inspectorRunId: null }),

  setAudioMuted: (v) => {
    saveBool(LS_AUDIO_MUTED, v)
    set({ audioMuted: v })
  },
  setMasterVolume: (v) => {
    const clamped = Math.max(0, Math.min(1, v))
    saveVolume(clamped)
    set({ masterVolume: clamped })
  },
  setViewBobEnabled: (v) => {
    saveBool(LS_VIEW_BOB, v)
    set({ viewBobEnabled: v })
  },

  setPlayerPose: (x, z, yaw) => {
    // Dead-zone: skip the set() (and the re-render it triggers on the minimap /
    // callout) when the player has barely moved. Position in metres, yaw in rad.
    const { playerX, playerZ, playerYaw } = get()
    if (
      Math.abs(x - playerX) < 0.03 &&
      Math.abs(z - playerZ) < 0.03 &&
      Math.abs(yaw - playerYaw) < 0.01
    ) return
    set({ playerX: x, playerZ: z, playerYaw: yaw })
  },

  toggleMinimap: () => set((state) => {
    const next = !state.minimapVisible
    saveBool(LS_MINIMAP, next)
    return { minimapVisible: next }
  }),

  dismissOnboarding: () => {
    if (get().onboardingDismissed) return
    saveBool(LS_ONBOARDED, true)
    set({ onboardingDismissed: true })
  },

  addToast: (t) => set((state) => {
    const id = `toast-${++toastSeq}`
    // Keep at most 2 existing + 1 new = max 3 stacked; drop oldest first
    const prev = state.toasts.length >= 3 ? state.toasts.slice(-(3 - 1)) : state.toasts
    return { toasts: [...prev, { ...t, id }] }
  }),
  dismissToast: (id) => set((state) => ({ toasts: state.toasts.filter(t => t.id !== id) })),

  hydrate: async () => {
    const {
      apiClient,
      apiGeneration,
      interactionsByRun: previousInteractions,
    } = get()
    if (!apiClient) return
    if (typeof apiClient.loadProjection === 'function') {
      const projection = await apiClient.loadProjection()
      if (
        get().apiClient !== apiClient ||
        get().apiGeneration !== apiGeneration
      ) return
      const next = bucketInteractions(projection.runs, projection.interactions)
      set((state) => ({
        remoteProjectionStatus: 'fresh',
        remoteProjectionCapturedAt: projection.capturedAt,
        runs: projection.runs,
        queue: projection.queue,
        health: projection.health,
        tasks: projection.tasks,
        knowledge: projection.knowledge,
        agents: projection.agents,
        chats: projection.chats,
        subjectLaunchesByTaskId: reconcileSubjectLaunches(
          state.subjectLaunchesByTaskId,
          projection.runs,
        ),
        interactionsByRun: sameInteractions(previousInteractions, next)
          ? previousInteractions
          : next,
      }))
      return
    }
    const [runs, queue, health, tasks, knowledge, agents, chats] = await Promise.all([
      apiClient.listRuns(),
      apiClient.listQueue(),
      apiClient.getDaemonHealth(),
      typeof apiClient.listTasks === 'function'
        ? apiClient.listTasks()
        : Promise.resolve([]),
      typeof apiClient.listKnowledge === 'function'
        ? apiClient.listKnowledge()
        : Promise.resolve([]),
      typeof apiClient.listAgents === 'function'
        ? apiClient.listAgents()
        : Promise.resolve([]),
      typeof apiClient.listChats === 'function'
        ? apiClient.listChats().catch(() => [])
        : Promise.resolve([]),
    ])

    // The token/client may have changed while the first request group was in
    // flight. Do not issue follow-up work or publish data owned by that session.
    if (
      get().apiClient !== apiClient ||
      get().apiGeneration !== apiGeneration
    ) return

    // ONE fetch for all runs (the endpoint returns every interaction; the old
    // per-run loop issued N identical requests), bucketed client-side.
    const activeRuns = runs.filter((r) => r.status === 'running')
    let next: Record<string, Interaction[]>
    try {
      const all = await apiClient.listInteractions('')
      next = bucketInteractions(activeRuns, all)
    } catch {
      // A failed fetch must NOT read as "no pending questions" — that flips a
      // waiting character from standing back into its chair and back again on
      // the next poll. Carry the previous snapshot forward instead.
      next = previousInteractions
    }

    // A second ownership check covers replacement during the interactions
    // request. Publish the complete projection atomically only for its owner.
    if (
      get().apiClient !== apiClient ||
      get().apiGeneration !== apiGeneration
    ) return

    // Reuse the previous object identity when nothing actually changed, so
    // world systems subscribed on reference equality don't re-run.
    set((state) => ({
      remoteProjectionStatus: 'fresh',
      remoteProjectionCapturedAt: new Date().toISOString(),
      runs,
      queue,
      health,
      tasks,
      knowledge,
      agents,
      chats,
      subjectLaunchesByTaskId: reconcileSubjectLaunches(
        state.subjectLaunchesByTaskId,
        runs,
      ),
      interactionsByRun: sameInteractions(previousInteractions, next)
        ? previousInteractions
        : next,
    }))
  },

  launchTaskRun: async (taskId) => {
    const { apiClient, apiGeneration, tasks } = get()
    const task = tasks.find((candidate) => candidate.id === taskId)
    if (!apiClient || !apiClient.launchSubjectRun) {
      return { status: 'failed', reason: 'unavailable' }
    }
    if (!task?.canLaunch) {
      return { status: 'failed', reason: 'not-authorized' }
    }
    const stillOwned = (): boolean =>
      get().apiClient === apiClient && get().apiGeneration === apiGeneration

    set((state) => ({
      subjectLaunchesByTaskId: {
        ...state.subjectLaunchesByTaskId,
        [taskId]: { status: 'pending' },
      },
    }))

    let run: NormalizedRun
    try {
      run = await apiClient.launchSubjectRun('task', taskId)
    } catch (error) {
      if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
      const status = responseStatus(error)
      const reason: SubjectLaunchFailure =
        status === 401 || status === 403
          ? 'not-authorized'
          : status === 409
            ? 'conflict'
            : 'unavailable'
      set((state) => ({
        subjectLaunchesByTaskId: {
          ...state.subjectLaunchesByTaskId,
          [taskId]: { status: 'failed', reason },
        },
      }))
      return { status: 'failed', reason }
    }

    if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
    set((state) => ({
      subjectLaunchesByTaskId: {
        ...state.subjectLaunchesByTaskId,
        [taskId]: { status: 'accepted', runId: run.id, observed: false },
      },
    }))

    try {
      await get().hydrate()
    } catch {
      // A successful command is not rewritten as failure when refresh is
      // temporarily unavailable. The world still waits for authoritative data.
    }
    if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
    const observed = get().runs.some((candidate) => candidate.id === run.id)
    set((state) => ({
      subjectLaunchesByTaskId: {
        ...state.subjectLaunchesByTaskId,
        [taskId]: { status: 'accepted', runId: run.id, observed },
      },
    }))
    return { status: 'accepted', runId: run.id, observed }
  },

  loadRunDetail: async (runId) => {
    const { apiClient, apiGeneration } = get()
    if (!apiClient) return
    try {
      const detail = await apiClient.getRun(runId)
      if (
        get().apiClient !== apiClient ||
        get().apiGeneration !== apiGeneration
      ) return
      set((state) => ({
        runDetailsById: { ...state.runDetailsById, [runId]: detail },
      }))
    } catch {
      // Inspector retains list projection or last-good detail.
    }
  },

  appendTranscriptEvent: (event) =>
    set((state) => {
      const prev = state.transcriptsByRun[event.runId] ?? []
      return {
        transcriptsByRun: {
          ...state.transcriptsByRun,
          // Cap at 50 events per run to bound memory
          [event.runId]: [...prev.slice(-49), event],
        },
      }
    }),

  replaceTranscriptEvents: (runId, events) =>
    set((state) => ({
      transcriptsByRun: {
        ...state.transcriptsByRun,
        [runId]: events.slice(-50),
      },
    })),

  setInteractionsForRun: (runId, interactions) =>
    set((state) => ({
      interactionsByRun: { ...state.interactionsByRun, [runId]: interactions },
    })),

  submitInteractionResponse: async (interaction, answer) => {
    const { apiClient, apiGeneration } = get()
    if (!apiClient) return { status: 'failed', reason: 'unavailable' }
    const stillOwned = (): boolean =>
      get().apiClient === apiClient && get().apiGeneration === apiGeneration

    try {
      await apiClient.respondToInteraction(interaction.id, answer)
    } catch (error) {
      if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
      const status = responseStatus(error)
      if (status !== 409) {
        return {
          status: 'failed',
          reason: status === 401 || status === 403
            ? 'not-authorized'
            : 'unavailable',
        }
      }

      // A conflict can mean another actor answered first, but the HTTP status
      // alone is not proof. Refresh and close only when authoritative state no
      // longer contains this interaction.
      try {
        await get().hydrate()
      } catch {
        return { status: 'failed', reason: 'conflict' }
      }
      if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
      const remains = (get().interactionsByRun[interaction.runId] ?? [])
        .some((candidate) => candidate.id === interaction.id)
      return remains
        ? { status: 'failed', reason: 'conflict' }
        : { status: 'already-answered', refreshed: true }
    }

    if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
    set((state) => ({
      interactionsByRun: {
        ...state.interactionsByRun,
        [interaction.runId]: (state.interactionsByRun[interaction.runId] ?? [])
          .filter((candidate) => candidate.id !== interaction.id),
      },
    }))

    let refreshed = true
    try {
      await get().hydrate()
    } catch {
      refreshed = false
    }
    if (!stillOwned()) return { status: 'failed', reason: 'session-changed' }
    return { status: 'accepted', refreshed }
  },
}))
